/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbItemUtils } from "abreact/misc/AbItemUtils";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { AbItemSeparator } from "abweb/collection/AbItemSeparator";
import * as React from "react";
import { KeyValue } from "abweb/utils/KeyboardUtils";

/**
 * Helper to sequentially scan an array of items with format [string, originalIndex], where the items have been sorted
 * lexicographically by string. The function scans the array from a starting index in both directions until items'
 * string no longer matches the prefix. It returns the smallest originalIndex it finds that is at least minOriginalIndex,
 * and the endpoints of the range it scanned.
 * If no valid item is found, return undefined.
 * @param mid
 * @param stringArr
 * @param prefix
 * @param minOriginalIndex
 */
const scanMinIndex = (start, stringArr, prefix, minOriginalIndex = -1) => {
  // we've found an item that matches the prefix through binary search
  // now, we linearly scan the alphanumerically sorted list from this item in both directions
  // and find the matching item with the minimum index in the *original* list

  let lo = start;
  let hi = start;
  let minValidIndex = stringArr[start][1] >= minOriginalIndex ? stringArr[start][1] : undefined;
  while (lo > 0 && stringArr[lo - 1][0].startsWith(prefix)) {
    lo--;
    const origIndexHere = stringArr[lo][1];
    if ((minValidIndex === undefined || minValidIndex !== undefined && origIndexHere < minValidIndex) && origIndexHere >= minOriginalIndex) {
      minValidIndex = origIndexHere;
    }
  }
  while (hi < stringArr.length - 1 && stringArr[hi + 1][0].startsWith(prefix)) {
    hi++;
    const origIndexHere = stringArr[hi][1];
    if ((minValidIndex === undefined || minValidIndex !== undefined && origIndexHere < minValidIndex) && origIndexHere >= minOriginalIndex) {
      minValidIndex = origIndexHere;
    }
  }
  return minValidIndex !== undefined ? [minValidIndex, lo, hi] : undefined;
};

/**
 * Utility to efficiently find the item matching a given prefix with the smallest *original* index in an array.
 * @param stringArr A *sorted* array of strings, with items formatted as [stringValue, originalArrayIndex]
 * @param prefix Prefix to match with an item.
 * @param startIndex Index of the *sorted* array at which to start the binary search.
 * @param endIndex Index of the *sorted* array at which to end the binary search.
 * @param origIndexMin To match, an item must also have an original index which is at least this.
 * @return Three-value array. The first is the *original* array index of the matching item with the smallest
 * original index. The second and third are the start and end indices in the *sorted* array representing the range in
 * this array of items that match the prefix. If no match found, return undefined.
 */
const prefixBinarySearchScan = (stringArr, prefix, startIndex, endIndex) => {
  // if the prefix is empty, all strings match
  if (prefix === "") {
    return [0, 0, stringArr.length - 1];
  }

  // binary search over the sorted strings
  let start = startIndex ?? 0;
  let end = endIndex ?? stringArr.length - 1;
  while (start <= end) {
    const mid = Math.floor((start + end) / 2);
    if (stringArr[mid][0].startsWith(prefix)) {
      return scanMinIndex(mid, stringArr, prefix);
    } else if (stringArr[mid][0] < prefix) {
      start = mid + 1;
    } else {
      end = mid - 1;
    }
  }
  return undefined; // binary search found nothing
};
export let TypeToJumpUtils;
(function (_TypeToJumpUtils) {
  const itemToStringEnrichedOnly = _TypeToJumpUtils.itemToStringEnrichedOnly = item => {
    const enrichment = AbEnriched.getEnrichment(item);
    if (AbEnriched.isSelectable(enrichment)) {
      return enrichment?.displayName ?? "";
    }
    return "";
  };
  const defaultItemToString = _TypeToJumpUtils.defaultItemToString = maybeEnriched => {
    const {
      item,
      enrichment
    } = AbEnriched.extract(maybeEnriched);
    if (AbEnriched.isSelectable(enrichment)) {
      return AbItemUtils.defaultItemDisplayName(item, enrichment).trim();
    }
    return "";
  };
  const useTypeToJumpPrefixChooser = _TypeToJumpUtils.useTypeToJumpPrefixChooser = (items, getCurrentIndex, itemToString = TypeToJumpUtils.defaultItemToString, dataFreshness) => {
    // initial calculations (expensive, only run on first render and when items change)
    const sortedItems = React.useMemo(() => {
      const filteredItems = AbListHelper.from(items).filterType(AbItemSeparator.isNotSeparator).map((item, index) => [itemToString(item).toLowerCase(), index]).toArray();
      return filteredItems.sort((a, b) => a[0] < b[0] ? -1 : 1);
    }, [itemToString, items]);

    // return jumpOnKeypress function to call when a valid key is pressed and the caller component is selected
    return (currentString, typedChar) => {
      const newChar = typedChar.toLowerCase();
      const currentIndex = getCurrentIndex();
      let newString = currentString + newChar;

      // Try to find a match with the string of recently-typed characters.
      // If this fails, iteratively delete characters from the start of this string.
      let prevIndex = undefined;
      if (newString.length > 0) {
        // Try to find an item that prefix-matches the string of recently-typed characters.
        const binSearchResult = prefixBinarySearchScan(sortedItems, currentString); // no use for prevIndex; store matching range for currentString
        if (binSearchResult !== undefined) {
          const [foundPrefixIndex, matchRangeLo, matchRangeHi] = binSearchResult;
          prevIndex = foundPrefixIndex;
          const listIndex = prefixBinarySearchScan(sortedItems, newString, matchRangeLo, matchRangeHi)?.[0]; // pick up binary search where we left off, handling the last char
          if (listIndex !== undefined) {
            return [listIndex, newString]; // found an item with matching prefix
          }
        }

        /* if typed the same character twice in a row and no prefix matches found, try finding an item after the one
        currently chosen that prefix-matches currentString.
        For example, typing "unii" in an alphabetical list of countries with "United Kingdom" already chosen will jump
        to "United States". */
        if (newChar === currentString.charAt(currentString.length - 1) && prevIndex !== undefined) {
          // call the binary search function over the range of matches for currentString we found before
          // in order to efficiently find some matching entry after currentIndex (with the lowest original list index available)
          const sameLetterListIndex = scanMinIndex(prevIndex, sortedItems, currentString, currentIndex + 1)?.[0];
          if (sameLetterListIndex !== undefined) {
            return [sameLetterListIndex, currentString];
          }
        }

        // If still have found no matches, delete a letter from our string of recently-typed characters and try again.
        newString = newString.substr(1);
      }

      // try matching with letters successively removed from the start of the string
      while (newString.length > 0) {
        // Try to find an item that prefix-matches the string of recently-typed characters.
        const listIndex = prefixBinarySearchScan(sortedItems, newString)?.[0]; // no use for prevIndex; store matching range for currentString
        if (listIndex !== undefined) {
          return [listIndex, newString]; // found an item with matching prefix
        }
        newString = newString.substr(1); // no matches found
      }
      return [-1, ""]; // no matches found for any sequence of recently-typed characters
    };
  };
  const DONT_CLEAR_STRING_KEYS = _TypeToJumpUtils.DONT_CLEAR_STRING_KEYS = [KeyValue.SHIFT, KeyValue.CONTROL, KeyValue.FN, KeyValue.META];
})(TypeToJumpUtils || (TypeToJumpUtils = {}));
//# sourceMappingURL=TypeToJumpUtils.js.map
